import { usePerson, usePersonConnections, usePersonDocuments, useDocument } from '../hooks/useApi';
import { resolveImageUrl } from '../api';

interface DetailPanelProps {
  slug: string;
  type: string;
  onNavigate: (type: string, id: string) => void;
  onClose: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  politician: '#ED4245', business: '#3BA55C', royalty: '#FAA61A',
  celebrity: '#5865F2', associate: '#EB459E', legal: '#9B59B6',
  academic: '#1ABC9C', socialite: '#E67E22', 'military-intelligence': '#2C3E50',
  other: '#95A5A6',
};

export default function DetailPanel({ slug, type, onNavigate, onClose }: DetailPanelProps) {
  if (!slug) return null;

  if (type === 'person') return <PersonDetail slug={slug} onNavigate={onNavigate} onClose={onClose} />;
  if (type === 'document') return <DocumentDetail id={slug} onNavigate={onNavigate} onClose={onClose} />;

  return (
    <div className="detail-panel">
      <button className="detail-close" onClick={onClose}>x</button>
      <p>Select an entity to view details</p>
    </div>
  );
}

function PersonDetail({ slug, onNavigate, onClose }: { slug: string; onNavigate: (t: string, id: string) => void; onClose: () => void }) {
  const { data: personRes, isLoading } = usePerson(slug);
  const { data: connRes } = usePersonConnections(slug, { limit: 10 });
  const { data: docsRes } = usePersonDocuments(slug, { per_page: 5 });

  const person = personRes?.data;
  const connections = connRes?.data || [];
  const docs = docsRes?.data || [];

  if (isLoading) return <div className="detail-panel"><div className="detail-loading">Loading...</div></div>;
  if (!person) return <div className="detail-panel"><p>Person not found</p></div>;

  const catColor = CATEGORY_COLORS[person.category] || '#95A5A6';

  return (
    <div className="detail-panel">
      <button className="detail-close" onClick={onClose}>&times;</button>

      <div className="detail-header">
        {resolveImageUrl(person.image_url) && (
          <img className="detail-avatar" src={resolveImageUrl(person.image_url)!} alt={person.name} />
        )}
        <div>
          <h2 className="detail-name">{person.name}</h2>
          <span className="detail-category" style={{ backgroundColor: catColor }}>
            {person.category}
          </span>
          {person.black_book_entry && <span className="detail-bb">Black Book</span>}
        </div>
      </div>

      {person.aliases?.length > 0 && (
        <div className="detail-aliases">
          AKA: {person.aliases.join(', ')}
        </div>
      )}

      <div className="detail-stats-grid">
        <div className="stat-box">
          <span className="stat-number">{person.stats.documents.toLocaleString()}</span>
          <span className="stat-label">Documents</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{person.stats.flights}</span>
          <span className="stat-label">Flights</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{person.stats.connections}</span>
          <span className="stat-label">Connections</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{person.stats.emails}</span>
          <span className="stat-label">Emails</span>
        </div>
      </div>

      {person.bio && (
        <div className="detail-section">
          <h3>Biography</h3>
          <p className="detail-bio">{person.bio}</p>
        </div>
      )}

      {person.notable_positions?.length > 0 && (
        <div className="detail-section">
          <h3>Positions</h3>
          <ul className="detail-positions">
            {person.notable_positions.map((pos, i) => (
              <li key={i}>{pos}</li>
            ))}
          </ul>
        </div>
      )}

      {connections.length > 0 && (
        <div className="detail-section">
          <h3>Top Connections</h3>
          <div className="detail-connections">
            {connections.map((conn) => (
              <button
                key={conn.target_id}
                className="connection-chip"
                onClick={() => onNavigate('person', conn.target_slug)}
              >
                <span className={`strength-dot ${conn.strength}`} />
                <span>{conn.target_name}</span>
                <span className="conn-count">{conn.co_documents} docs</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div className="detail-section">
          <h3>Recent Documents</h3>
          <div className="detail-docs">
            {docs.map((doc: any) => (
              <button
                key={doc.id}
                className="doc-chip"
                onClick={() => onNavigate('document', doc.id)}
              >
                <span className="doc-title">{doc.title}</span>
                <span className="doc-date">{doc.date || 'N/A'}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="detail-actions">
        <a
          href={person.url}
          target="_blank"
          rel="noopener noreferrer"
          className="action-btn primary"
        >
          View on Site
        </a>
      </div>
    </div>
  );
}

function DocumentDetail({ id, onNavigate, onClose }: { id: string; onNavigate: (t: string, id: string) => void; onClose: () => void }) {
  const { data: docRes, isLoading } = useDocument(id);
  const doc = docRes?.data;

  if (isLoading) return <div className="detail-panel"><div className="detail-loading">Loading...</div></div>;
  if (!doc) return <div className="detail-panel"><p>Document not found</p></div>;

  return (
    <div className="detail-panel">
      <button className="detail-close" onClick={onClose}>&times;</button>

      <div className="detail-header">
        <div>
          <h2 className="detail-name">{doc.title}</h2>
          <div className="detail-doc-meta">
            <span className="detail-category" style={{ backgroundColor: '#5865F2' }}>{doc.category}</span>
            <span className="detail-source">{doc.source}</span>
            {doc.date && <span className="detail-date">{doc.date}</span>}
          </div>
        </div>
      </div>

      <div className="detail-stats-grid">
        <div className="stat-box">
          <span className="stat-number">{doc.page_count || '-'}</span>
          <span className="stat-label">Pages</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{doc.person_count}</span>
          <span className="stat-label">Persons</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{doc.upvotes}</span>
          <span className="stat-label">Upvotes</span>
        </div>
      </div>

      {doc.summary && (
        <div className="detail-section">
          <h3>Summary</h3>
          <p className="detail-bio">{doc.summary}</p>
        </div>
      )}

      {doc.bates_range && (
        <div className="detail-section">
          <h3>Bates Range</h3>
          <code className="bates-code">{doc.bates_range}</code>
        </div>
      )}

      {doc.tags?.length > 0 && (
        <div className="detail-section">
          <h3>Tags</h3>
          <div className="tag-list">
            {doc.tags.map((tag) => (
              <span key={tag} className="tag-chip">{tag}</span>
            ))}
          </div>
        </div>
      )}

      <div className="detail-actions">
        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="action-btn primary">
          View on Site
        </a>
        {doc.pdf_url && (
          <a href={doc.pdf_url} target="_blank" rel="noopener noreferrer" className="action-btn secondary">
            View PDF
          </a>
        )}
      </div>
    </div>
  );
}
